import { useMemo } from "react";
import type { ObjectDiff, RuleDiffEvent } from "@rebuild/shared";
import { useOrganizations } from "../../hooks/rebuildBackendFetchHooks";
import { useTpas } from "../../hooks/useTpas";
import { useCarriers } from "../../hooks/useCarriers";

interface RuleAuditTrailProps {
  auditTrail?: RuleDiffEvent[];
}

type FormatDiffValue = (path: string, value: unknown) => string;

const TOP_LEVEL_FIELD_LABELS: Record<string, string> = {
  name: "Name",
  isDev: "Is Dev Rule",
  isDisabled: "Is Disabled",
  archived: "Archived",
  templateId: "Template",
  runCriteria: "Run Criteria",
  ruleInputs: "Input Parameters",
  isDraft: "Draft",
};

const RUN_CRITERIA_FIELD_LABELS: Record<string, string> = {
  organizations: "Organizations",
  tpas: "TPAs",
  carriers: "Carriers",
  catNumbers: "Cat Numbers",
  estimateType: "Estimate Type",
  isMitToRecon: "Mit → Recon",
};

// The run-criteria fields whose value is a `{ includeOrExclude, ids }` object of entity ids we resolve to names.
const ID_RESOLVABLE_CRITERIA = new Set(["organizations", "tpas", "carriers"]);

// The backend diffs runCriteria at field granularity, so a change normally arrives as a whole-field diff (path
// `runCriteria.organizations`, value `{ includeOrExclude, ids }`). We also defensively handle leaf diffs (path
// `runCriteria.organizations.ids.1`, value a bare id, or `...includeOrExclude`) in case any entry carries them.
// Split the path so we can handle both.
function runCriteriaParts(path: string): { field: string; leaf: string[] } | undefined {
  const parts = path.split(".");
  if (parts[0] !== "runCriteria" || parts.length < 2) return undefined;
  return { field: parts[1], leaf: parts.slice(2) };
}

function formatFieldPath(path: string): string {
  if (!path) return "Rule";
  const rc = runCriteriaParts(path);
  if (rc) {
    const label = RUN_CRITERIA_FIELD_LABELS[rc.field] ?? rc.field;
    return rc.leaf[0] === "includeOrExclude" ? `Run Criteria › ${label} (mode)` : `Run Criteria › ${label}`;
  }
  const [head, ...rest] = path.split(".");
  const headLabel = TOP_LEVEL_FIELD_LABELS[head] ?? head;
  return rest.length ? `• ${headLabel} › ${rest.join(" › ")}` : headLabel;
}

function formatPlainValue(value: unknown): string {
  if (value === undefined) return "∅";
  if (value === null) return "null";
  if (Array.isArray(value)) return value.map(formatPlainValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function includeVerb(includeOrExclude: unknown): string {
  return includeOrExclude === "EXCLUDE" ? "EXCLUDES" : "INCLUDES";
}

// Render a whole `{ includeOrExclude, ids }` run-criteria field as e.g. "INCLUDES: Acme, Globex", resolving each id to
// a name and falling back to the raw id when no name is found.
function formatCriteriaField(value: unknown, resolve: (id: string) => string): string {
  if (!value || typeof value !== "object") return formatPlainValue(value);
  const { includeOrExclude, ids } = value as { includeOrExclude?: string; ids?: string[] };
  const names = (ids ?? []).map((id) => resolve(id));
  return `${includeVerb(includeOrExclude)}: ${names.length ? names.join(", ") : "—"}`;
}

export function formatAuthor(event: RuleDiffEvent): string {
  const name = [event.userFirstName, event.userLastName].filter(Boolean).join(" ").trim();
  return name || event.userUid;
}

function formatDateTime(dateTime: number): string {
  return new Date(dateTime).toLocaleString();
}

const FieldDiffRow: React.FC<{ diff: ObjectDiff; formatDiffValue: FormatDiffValue }> = ({ diff, formatDiffValue }) => {
  const oldStr = formatDiffValue(diff.path, diff.oldValue);
  const newStr = formatDiffValue(diff.path, diff.newValue);
  return (
    <div className="text-sm text-gray-700">
      <span className="font-medium">{formatFieldPath(diff.path)}:</span>{" "}
      {diff.type === "added" ? (
        <span className="text-green-700">{newStr}</span>
      ) : diff.type === "removed" ? (
        <span className="text-red-700 line-through">{oldStr}</span>
      ) : (
        <>
          <span className="text-red-700 line-through">{oldStr}</span> <span className="text-gray-400">→</span>{" "}
          <span className="text-green-700">{newStr}</span>
        </>
      )}
    </div>
  );
};

const AuditEventNode: React.FC<{ event: RuleDiffEvent; isLast: boolean; formatDiffValue: FormatDiffValue }> = ({
  event,
  isLast,
  formatDiffValue,
}) => {
  const isCreated = event.eventType === "created";
  return (
    <div className="relative pl-6">
      {/* timeline line + dot */}
      {!isLast && <div className="absolute left-[6px] top-4 -bottom-6 w-px bg-gray-200" />}
      <div
        className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 ${isCreated ? "border-blue-500 bg-blue-100" : "border-gray-400 bg-white"}`}
      />

      <div className={`mb-6 rounded-lg border p-3 ${isCreated ? "border-blue-100 bg-blue-50/60" : "border-gray-100 bg-gray-50/60"}`}>
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 text-xs font-semibold ${isCreated ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}
          >
            {isCreated ? "Created" : "Updated"}
          </span>
          <span className="text-xs text-gray-500">{formatDateTime(event.dateTime)}</span>
        </div>

        {event.diffs.length > 0 ? (
          <>
            <div className="mt-1 text-sm text-gray-700">
              <span className="font-bold">{formatAuthor(event)}</span> made the following changes:
            </div>
            <div className="mt-1 space-y-1 pl-4">
              {event.diffs.map((diff, i) => (
                <FieldDiffRow key={`${diff.path}_${i}`} diff={diff} formatDiffValue={formatDiffValue} />
              ))}
            </div>
          </>
        ) : (
          <div className="mt-1 text-sm text-gray-600">
            by <span className="font-bold text-gray-700">{formatAuthor(event)}</span>
          </div>
        )}

        {event.note && (
          <>
            <div className="mt-2 text-sm text-gray-700">With the notes:</div>
            <div className="mt-1 whitespace-pre-wrap rounded border border-gray-100 bg-white p-2 text-sm text-gray-800">{event.note}</div>
          </>
        )}
      </div>
    </div>
  );
};

export const RuleAuditTrail: React.FC<RuleAuditTrailProps> = ({ auditTrail }) => {
  const { data: organizations } = useOrganizations();
  const { tpas } = useTpas();
  const { carriers } = useCarriers();

  const nameById = useMemo(() => {
    const orgs = new Map((organizations ?? []).map((o) => [o.organizationId, o.organizationName ?? o.organizationId]));
    const tpaMap = new Map((tpas ?? []).map((t) => [t.id, t.name]));
    const carrierMap = new Map((carriers ?? []).map((c) => [c.id, c.name]));
    return { organizations: orgs, tpas: tpaMap, carriers: carrierMap };
  }, [organizations, tpas, carriers]);

  // Resolve run-criteria fields to names (handling both whole-field and leaf diffs); everything else is a plain value.
  const formatDiffValue = useMemo<FormatDiffValue>(
    () => (path, value) => {
      const rc = runCriteriaParts(path);
      if (rc && ID_RESOLVABLE_CRITERIA.has(rc.field)) {
        const lookup = nameById[rc.field as keyof typeof nameById];
        const resolve = (id: string) => lookup.get(id) ?? id;

        if (rc.leaf.length === 0) return formatCriteriaField(value, resolve);
        if (rc.leaf[0] === "includeOrExclude") return includeVerb(value);
        if (rc.leaf[0] === "ids") {
          if (Array.isArray(value)) return value.map((id) => resolve(String(id))).join(", ");
          if (typeof value === "string") return resolve(value);
        }
        // any other shape (e.g. the whole field object arriving on a nested leaf) — format what we can
        return typeof value === "object" && value !== null ? formatCriteriaField(value, resolve) : formatPlainValue(value);
      }
      return formatPlainValue(value);
    },
    [nameById],
  );

  // newest first, without mutating the prop
  const events = useMemo(() => [...(auditTrail ?? [])].sort((a, b) => b.dateTime - a.dateTime), [auditTrail]);

  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-4 shrink-0 text-lg font-medium">Change History</h3>
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {events.length === 0 ? (
          <p className="text-sm text-gray-400">No history yet — it will appear here after you save.</p>
        ) : (
          events.map((event, i) => (
            <AuditEventNode
              key={`${event.dateTime}_${event.note}`}
              event={event}
              isLast={i === events.length - 1}
              formatDiffValue={formatDiffValue}
            />
          ))
        )}
      </div>
    </div>
  );
};
